import { useCallback, useEffect, useRef, useState } from 'react';

const MIN_WIDTH = 400;
const MAX_VIEWPORT_RATIO = 0.8;
const DEFAULT_WIDTH = 500;

function getMaxWidth(): number {
  if (typeof window === 'undefined') return DEFAULT_WIDTH;
  return Math.floor(window.innerWidth * MAX_VIEWPORT_RATIO);
}

function clamp(width: number): number {
  return Math.min(Math.max(width, MIN_WIDTH), getMaxWidth());
}

function readStoredWidth(key: string): number {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return DEFAULT_WIDTH;
    const parsed = Number.parseInt(raw, 10);
    if (!Number.isFinite(parsed)) return DEFAULT_WIDTH;
    return clamp(parsed);
  } catch {
    return DEFAULT_WIDTH;
  }
}

function readStoredMaximized(key: string): boolean {
  try {
    return localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
}

function writeStoredWidth(key: string, width: number): void {
  try {
    localStorage.setItem(key, String(width));
  } catch {
    // ignore quota / private-mode failures
  }
}

function writeStoredMaximized(key: string, value: boolean): void {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // ignore
  }
}

export interface UseResizableArtifactResult {
  width: number;
  isMaximized: boolean;
  onHandlePointerDown: (e: React.PointerEvent<HTMLDivElement>) => void;
  toggleMaximize: () => void;
}

export function useResizableArtifact(storagePrefix: string): UseResizableArtifactResult {
  const widthKey = `${storagePrefix}.width`;
  const maximizedKey = `${storagePrefix}.maximized`;

  const [isMaximized, setIsMaximized] = useState<boolean>(() => readStoredMaximized(maximizedKey));
  const [width, setWidth] = useState<number>(() => {
    const stored = readStoredWidth(widthKey);
    return readStoredMaximized(maximizedKey) ? getMaxWidth() : stored;
  });

  const priorWidthRef = useRef<number>(readStoredWidth(widthKey));

  useEffect(() => {
    function onResize() {
      setWidth((current) => (isMaximized ? getMaxWidth() : clamp(current)));
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isMaximized]);

  const onHandlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = width;
      const previousUserSelect = document.body.style.userSelect;
      document.body.style.userSelect = 'none';

      function onMove(ev: PointerEvent) {
        const next = clamp(startWidth + (startX - ev.clientX));
        setWidth(next);
      }

      function onUp(ev: PointerEvent) {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        document.body.style.userSelect = previousUserSelect;
        const finalWidth = clamp(startWidth + (startX - ev.clientX));
        priorWidthRef.current = finalWidth;
        writeStoredWidth(widthKey, finalWidth);
        if (isMaximized) {
          setIsMaximized(false);
          writeStoredMaximized(maximizedKey, false);
        }
      }

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [width, isMaximized, widthKey, maximizedKey]
  );

  const toggleMaximize = useCallback(() => {
    setIsMaximized((current) => {
      const next = !current;
      if (next) {
        priorWidthRef.current = width;
        setWidth(getMaxWidth());
      } else {
        setWidth(clamp(priorWidthRef.current));
      }
      writeStoredMaximized(maximizedKey, next);
      return next;
    });
  }, [width, maximizedKey]);

  return { width, isMaximized, onHandlePointerDown, toggleMaximize };
}
